import { Text, TextInput, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { formatRepsRange } from '@heraion/shared';

import { Badge } from '../primitives/Badge';
import type { DraftSet } from './state/loggerDraft';
import type { useTheme } from './theme';
import type { makeFocusPanelInputsStyles } from './focusPanelInputsStyles';
import { useSelectAllOnFocus } from './useSelectAllOnFocus';

// The REPS cell of FocusPanelInputs, with what the plan says about it on its
// label: the AMRAP floor and the rep range (#336 S3). Split out of
// FocusPanelInputs for the 500-line cap, at the seam between the reps cell and
// the weight cell; the parent keeps the row's error line and the focus
// reporter, which both cells share.
export function FocusPanelRepsField({
  set: s,
  invalid,
  onChange,
  fieldFocus,
  styles,
  theme,
}: {
  set: DraftSet;
  /** The reps text does not parse: the parent's error line says why. */
  invalid: boolean;
  onChange: (patch: Pick<DraftSet, 'reps'>) => void;
  fieldFocus: { onFocus: (id: string) => void; onBlur: (id: string) => void };
  styles: ReturnType<typeof makeFocusPanelInputsStyles>;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  // Tap a populated field: select the whole number so you type over it.
  const repsSel = useSelectAllOnFocus(s.reps);
  const range = s.repsRangePrescribed;
  return (
    <View style={styles.field}>
      {/* A PRESCRIBED AMRAP used to live only in the placeholder, so it
          vanished the moment the first digit was typed, exactly when you
          still need to know the target is "at least N, then as many as
          possible" (LG-23, inbox 3d88179b). The badge sits on the label and
          STAYS. It is display-only on purpose: <Badge> is the
          non-interactive status tag, never <Chip>. It reads
          `amrapPrescribed`, the PLAN's request, never the athlete's `amrap`
          outcome. The rep range (#336 S3) sits in the same place for the
          same reason: the target is prefilled, the range says how far from
          it is still on plan. The two never meet (Q6). */}
      <View style={styles.fieldLabelRow}>
        <Text style={styles.fieldLabel}>{t('mobile.logger.setReps')}</Text>
        {s.amrapPrescribed ? (
          <Badge
            theme={theme}
            // The floor rides on the badge, not only in the placeholder: a
            // placeholder dies on the first keystroke. Falls back to the bare
            // word when there is no floor (a chip-marked AMRAP nobody
            // prescribed).
            label={
              s.amrapFloor
                ? t('mobile.logger.amrapBadgeWithFloor', {
                    defaultValue: 'AMRAP ≥ {{reps}}',
                    reps: s.amrapFloor,
                  })
                : t('program.amrapBadge', { defaultValue: 'AMRAP' })
            }
            a11yLabel={
              s.amrapFloor
                ? t('mobile.logger.amrapPrescribedFloorA11y', {
                    defaultValue: 'AMRAP: at least {{reps}} reps, then as many as possible',
                    reps: s.amrapFloor,
                  })
                : t('mobile.logger.amrapPrescribedA11y', {
                    defaultValue: 'AMRAP: at least the target reps, then as many as possible',
                  })
            }
          />
        ) : range ? (
          <Badge
            theme={theme}
            label={formatRepsRange(range)}
            numeric
            a11yLabel={t('mobile.logger.repsRangeA11y', {
              defaultValue: 'Rep range {{min}} to {{max}}',
              min: range.min,
              max: range.max,
            })}
          />
        ) : null}
      </View>
      <TextInput
        ref={repsSel.ref}
        style={[styles.input, styles.inputNumeric, invalid && styles.inputInvalid]}
        keyboardType="number-pad"
        inputMode="numeric"
        // On a prescribed AMRAP the cell arrives EMPTY (LG-28) and the floor
        // lives here instead, as "≥ 8": a placeholder cannot be submitted by
        // doing nothing. Every other set keeps the bare dash: its cell is
        // already seeded with the prescribed reps.
        placeholder={
          s.amrapPrescribed && s.amrapFloor
            ? t('mobile.logger.amrapFloorPlaceholder', {
                defaultValue: '≥ {{reps}}',
                reps: s.amrapFloor,
              })
            : '\u2014'
        }
        placeholderTextColor={theme.palette.ink3}
        value={s.reps}
        onChangeText={(reps) => onChange({ reps })}
        selectTextOnFocus
        onFocus={() => {
          repsSel.onFocus();
          fieldFocus.onFocus('reps');
        }}
        onBlur={() => fieldFocus.onBlur('reps')}
        maxLength={4}
      />
    </View>
  );
}
